import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { IdToken } from '@auth0/auth0-spa-js';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AuthRedirectGuard,
  isInvalidTokenApiError,
} from './auth-redirect.guard';
import {
  clearStoredAuthTokens,
  getStoredAccessToken,
  hasValidStoredSession,
  isJwtExpired,
  navigateToForbidden,
} from './token.util';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

@Injectable()
export class BasePathInterceptor implements HttpInterceptor {
  constructor(
    private auth: AuthService,
    private router: Router,
    private authRedirectGuard: AuthRedirectGuard
  ) {
    console.log('BasePathInterceptor:: constructor called');
  }

  /** Refresh tokens via Auth0 and retry the request once after an invalid-token 401. */
  private retryWithFreshToken(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    attachToken: (accessToken: string, idToken: string) => HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    clearStoredAuthTokens();

    return combineLatest([
      this.auth.getAccessTokenSilently(),
      this.resolveIdToken(),
    ]).pipe(
      switchMap(([accessToken, idToken]) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('access_token', accessToken);

        const retryReq = attachToken(accessToken, idToken).clone({
          setHeaders: { [AUTH_RETRY_HEADER]: 'true' },
        });
        return next.handle(retryReq).pipe(
          catchError((retryError) => {
            const retryHttpError = retryError as { status?: number };
            if (retryHttpError.status === 401) {
              this.authRedirectGuard.redirectForTokenError(retryError);
            }
            return throwError(() => retryError);
          })
        );
      }),
      catchError((refreshError) => {
        this.authRedirectGuard.redirectForTokenError(refreshError);
        return throwError(() => refreshError);
      })
    );
  }

  /** Prefer a valid cached id token; otherwise read a fresh one from Auth0. */
  private resolveIdToken(): Observable<string> {
    const stored = localStorage.getItem('idToken') || '';
    if (stored && !isJwtExpired(stored)) {
      return of(stored);
    }
    if (stored) {
      localStorage.removeItem('idToken');
    }

    return this.auth.idTokenClaims$.pipe(
      filter((claims): claims is IdToken => !!claims?.__raw),
      take(1),
      switchMap((claims) => {
        const raw = claims.__raw ?? '';
        if (!raw || isJwtExpired(raw)) {
          return of('');
        }
        localStorage.setItem('idToken', raw);
        return of(raw);
      }),
      catchError(() => of(''))
    );
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!req.url || typeof req.url !== 'string') {
      return next.handle(req);
    }

    const isLocalAssetRequest =
      req.url.startsWith('/assets/') ||
      req.url.startsWith('assets/') ||
      req.url.includes('/assets/maps/');
    if (isLocalAssetRequest) {
      return next.handle(req);
    }

    const domain = environment.apiDomain;
    const version = environment.version;
    const app = environment.app;
    const baseUrl = `${domain}/${app}/${version}`;
    const dashboardApiBase = environment.dashboardApiBaseUrl?.replace(/\/$/, '') ?? '';
    const isDashboardApiRequest =
      !!dashboardApiBase && req.url.startsWith(dashboardApiBase);

    if (req.url.includes('https://') && !isDashboardApiRequest) {
      return next.handle(req);
    }

    const attachToken = (accessToken: string, idToken: string) => {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
      };

      if (isDashboardApiRequest) {
        return req.clone({
          setHeaders: {
            ...headers,
            id_token: idToken,
          },
        });
      }

      if (req.url.includes('http://')) {
        return req.clone({ setHeaders: headers });
      }

      return req.clone({
        url: baseUrl + req.url,
        setHeaders: {
          ...headers,
          id_token: idToken,
        },
      });
    };

    const handleApiError = (error: unknown) => {
      const httpError = error as { status?: number };

      if (httpError.status === 403) {
        navigateToForbidden(this.router);
        return throwError(() => error);
      }

      if (
        httpError.status === 401 &&
        isInvalidTokenApiError(error) &&
        !req.headers.has(AUTH_RETRY_HEADER)
      ) {
        return this.retryWithFreshToken(req, next, attachToken);
      }

      if (httpError.status === 401) {
        this.authRedirectGuard.redirectForTokenError(error);
      }

      return throwError(() => error);
    };

    const accessToken$ = hasValidStoredSession()
      ? of(getStoredAccessToken() as string)
      : this.auth.getAccessTokenSilently().pipe(
          catchError((tokenError) => {
            const cached = getStoredAccessToken();
            if (cached) {
              return of(cached);
            }
            clearStoredAuthTokens();
            return throwError(() => tokenError);
          })
        );

    return combineLatest([accessToken$, this.resolveIdToken()]).pipe(
      switchMap(([accessToken, idToken]) =>
        next.handle(attachToken(accessToken, idToken)).pipe(
          catchError(handleApiError)
        )
      ),
      catchError((tokenError) => {
        console.error('Token fetch error:', tokenError);
        this.authRedirectGuard.redirectForTokenError(tokenError);
        return throwError(() => tokenError);
      })
    );
  }
}
